<template>
    <div class="modal-overlay" @click.self="closeModal">
        <div class="modal">
            <div class="modal-header">
                <h1 class="modal-title">{{ addon ? 'Edytuj dodatek' : 'Dodaj dodatek' }}</h1>
                <button class="close-button" @click="closeModal" aria-label="Zamknij">
                    <font-awesome-icon icon="xmark" />
                </button>
            </div>

            <div class="modal-body">
                <div class="modal-scroll">
                    <form @submit.prevent="handleSubmit">
                        <div class="image-preview-wrapper" v-if="previewUrl || addon?.image_url">
                            <img :src="previewUrl || addon.image_url" alt="Podgląd zdjęcia" class="image-preview" />
                        </div>

                        <div class="form-grid">
                            <div class="form-group full-width">
                                <label>Zdjęcie</label>
                                <input type="file" accept="image/*" @change="handleFileChange" />
                            </div>

                            <div class="form-group">
                                <label>Kod *</label>
                                <input v-model="form.code" type="text" required />
                            </div>

                            <div class="form-group">
                                <label>Opis *</label>
                                <input v-model="form.desc" type="text" required />
                            </div>

                            <div class="form-group">
                                <label>Grupa *</label>
                                <input v-model="form.group" type="text" required />
                            </div>

                            <div class="form-group full-width">
                                <label>Wykluczenia (opcjonalnie)</label>
                                <ChipsAutocomplete v-model="form.excludes" :options="addonOptions" value-key="code"
                                    label-key="label" placeholder="Zacznij pisać kod lub opis dodatku..." />
                                <small class="hint">
                                    Enter/Tab lub klik na propozycję, aby dodać. Backspace usuwa ostatnie wykluczenie.
                                </small>
                            </div>
                        </div>

                        <div class="modal-actions">
                            <button type="submit" class="save-button" :disabled="addonStore.loading">
                                {{ addonStore.loading ? 'Zapisywanie...' : addon ? 'Zapisz zmiany' : 'Dodaj dodatek' }}
                            </button>
                            <button type="button" class="cancel-button" @click="closeModal">Anuluj</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useAddonStore } from '@/stores/addonStore'
import { showErrorToast, showWarningToast } from '@/utils/errorUtils'
import ChipsAutocomplete from '@/components/cms/ChipsAutocomplete.vue'

export default {
    components: { ChipsAutocomplete },
    props: {
        addon: Object
    },
    data() {
        return {
            addonStore: useAddonStore(),
            form: { code: '', desc: '', group: '', excludes: [], image: null },
            previewUrl: null
        }
    },
    computed: {
        addonOptions() {
            return this.addonStore.addons
                .filter(a => !this.addon || a.id !== this.addon.id)
                .map(a => ({ code: a.code, label: a.desc }))
                .filter(o => !!o.code);
        }
    },
    watch: {
        addon: {
            immediate: true,
            handler(addon) {
                if (addon) {
                    this.form = {
                        code: addon.code || '',
                        desc: addon.desc || '',
                        group: addon.group || '',
                        excludes: Array.isArray(addon.excludes) ? [...addon.excludes] : [],
                        image: null
                    }
                    this.previewUrl = addon.image_url || null
                } else {
                    this.resetForm()
                }
            }
        }
    },
    methods: {
        handleFileChange(event) {
            const file = event.target.files[0]
            if (file && file.type.startsWith('image/')) {
                this.form.image = file
                const reader = new FileReader()
                reader.onload = e => { this.previewUrl = e.target.result }
                reader.readAsDataURL(file)
            } else {
                this.form.image = null
                this.previewUrl = null
                showWarningToast('Wybierz poprawny plik graficzny (jpeg, png, gif itp.).')
            }
        },
        async handleSubmit() {
            try {
                const formData = new FormData()
                formData.append('code', this.form.code)
                formData.append('desc', this.form.desc)
                formData.append('group', this.form.group)

                if (this.form.excludes.length) {
                    this.form.excludes.forEach(item => formData.append('excludes[]', item))
                } else {
                    formData.append('excludes', '')
                }

                if (this.form.image instanceof File) formData.append('image', this.form.image)

                if (this.addon) await this.addonStore.updateAddon(this.addon.id, formData)
                else await this.addonStore.storeAddon(formData)

                this.$emit('saved')
                this.closeModal()
            } catch (err) {
                console.error('Błąd przy zapisie:', err.response?.data || err)
                showErrorToast(err, 'Wystąpił błąd podczas zapisu dodatku.')
            }
        },
        closeModal() {
            this.resetForm()
            this.$emit('close')
        },
        resetForm() {
            this.form = { code: '', desc: '', group: '', excludes: [], image: null }
            this.previewUrl = null
        }
    }
}
</script>

<style scoped lang="scss">
.modal {

    *,
    *::before,
    *::after {
        box-sizing: border-box;
    }
}

.modal-overlay {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 20px;
}

.modal {
    width: 100%;
    max-width: 700px;
    max-height: 90vh;
    background-color: $color-background;
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.modal-header {
    padding: 16px 20px;
    background: $color-primary;
    color: $color-white;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-title {
    font-size: 1.1rem;
    font-weight: 700;
    margin: 0;
}

.close-button {
    background: none;
    border: none;
    color: $color-white;
    font-size: 1.35rem;
    cursor: pointer;
    border-radius: 10px;
    padding: 6px;
    line-height: 1;
    transition: background-color .2s ease, transform .15s ease;

    &:hover {
        transform: scale(1.04);
    }

    &:focus-visible {
        outline: 2px solid $color-white;
        outline-offset: 2px;
    }
}

.modal-body {
    flex: 1;
    overflow: hidden;
}

.modal-scroll {
    padding: 20px;
    overflow-y: auto;
    max-height: calc(90vh - 70px);
    -webkit-overflow-scrolling: touch;
}

.image-preview-wrapper {
    text-align: center;
    margin-bottom: 1.2rem;

    .image-preview {
        max-width: 100%;
        height: auto;
        max-height: 220px;
        border-radius: 10px;
        border: 1px solid $color-grey;
        object-fit: contain;
        background-color: $color-background;
    }
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;

    .form-group.full-width {
        grid-column: span 2;
    }

    @media (max-width: 720px) {
        grid-template-columns: 1fr;

        .form-group.full-width {
            grid-column: span 1;
        }
    }
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
        font-weight: 600;
        font-size: .95rem;
        color: $color-text;
    }

    input,
    select,
    textarea {
        width: 100%;
        font-family: 'Inter', sans-serif;
        padding: 10px 12px;
        border: 2px solid $color-grey;
        border-radius: 10px;
        font-size: 1rem;
        transition: border .2s ease, box-shadow .2s ease;
        background: $color-white;

        &:focus {
            border-color: $color-primary;
            box-shadow: 0 0 0 4px rgba($color-primary, .12);
            outline: none;
        }
    }

    textarea {
        resize: vertical;
    }
}

.hint {
    color: $color-text-light;
    font-size: .85rem;
}

.modal-actions {
    display: flex;
    gap: 1rem;
    margin-top: 20px;

    button {
        flex: 1;
        padding: 12px 16px;
        border: none;
        border-radius: 10px;
        font-size: 1rem;
        font-weight: 600;
        cursor: pointer;
        transition: transform .05s ease, background-color .2s ease, opacity .2s ease;

        &:active {
            transform: translateY(1px);
        }
    }

    .save-button {
        background: $color-primary;
        color: $color-white;

        &:hover {
            background: $color-primary-dark;
        }

        &:disabled {
            opacity: .6;
            cursor: not-allowed;
        }
    }

    .cancel-button {
        background: $color-grey-light;
        color: $color-text;

        &:hover {
            background: $color-grey;
        }
    }

    @media (max-width: 720px) {
        flex-direction: column;

        button {
            width: 100%;
        }
    }
}
</style>
